/**
 * The new-game screen (ADR-0062): which civilisation to lead, and what kind of world.
 *
 * Shown when the game opens and from the New game button and the end screen. It only
 * gathers a setup; starting the game is the caller's job, through `start`.
 */

import type { Content } from '../../content/schema/index.js';
import { describeTrait, describeUnique } from './civ-text.js';

export interface GameSetup {
  seed: string;
  size: string;
  /** Civilisations in the world, the player's included. */
  civs: number;
  civType: string;
  mapType: string;
}

export interface NewGameScreen {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface NewGameScreenOptions {
  getContent: () => Content;
  start: (setup: GameSetup) => void;
  /** The newest autosave's turn, or null when there is none. */
  latestAutosave: () => Promise<number | null>;
  continueLatest: () => void;
}

const WORDS = ['meridian', 'lantern', 'basalt', 'harrow', 'kestrel', 'tessera', 'oxbow', 'cinder', 'vellum', 'quarry', 'saltmarsh', 'ember'];
export function randomSeed(): string {
  const pick = (): string => WORDS[Math.floor(Math.random() * WORDS.length)]!;
  return `${pick()}-${pick()}-${String(Math.floor(Math.random() * 100)).padStart(2, '0')}`;
}

export function createNewGameScreen(options: NewGameScreenOptions): NewGameScreen {
  const root = document.createElement('div');
  root.className = 'cities-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="cities-panel ng-panel" role="dialog" aria-labelledby="ng-title">
      <header>
        <h2 id="ng-title">New game</h2>
        <div class="cs-sub">Choose a people to lead, and the world they wake in.</div>
        <button id="ng-close" title="Close (Esc)">Close</button>
      </header>
      <div class="ng-body">
        <div class="ng-civs" id="ng-civs" role="radiogroup" aria-label="Civilisation"></div>
        <aside class="ng-world">
          <h3>World</h3>
          <div class="field"><label for="ng-size">Size</label><select id="ng-size"></select></div>
          <div class="field"><label for="ng-type">Kind</label><select id="ng-type"></select></div>
          <div class="dim ng-note" id="ng-type-note"></div>
          <div class="field"><label for="ng-rivals">Rivals</label><select id="ng-rivals"></select></div>
          <div class="field"><label for="ng-seed">Seed</label><input id="ng-seed" size="18" /></div>
          <div class="controls"><button id="ng-random" title="A different world">New seed</button></div>
          <div class="dim ng-note">The same seed and settings always make the same world and the same rivals.</div>
          <div class="ng-chosen" id="ng-chosen"></div>
          <button id="ng-start" class="primary ng-start">Start</button>
          <button id="ng-continue" class="ng-continue" hidden></button>
        </aside>
      </div>
    </div>
  `;
  document.body.append(root);
  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;

  let chosen = '';
  let built = false;

  function build(): void {
    const content = options.getContent();
    const sizes = content.mapgen.sizes;
    $('ng-size').innerHTML = sizes
      .map((s) => `<option value="${s.key}"${s.key === content.mapgen.defaults.size ? ' selected' : ''}>${escapeHtml(s.name)} — ${s.width}×${s.height}</option>`)
      .join('');
    $('ng-type').innerHTML = content.mapgen.mapTypes.map((t) => `<option value="${t.key}">${escapeHtml(t.name)}</option>`).join('');
    const most = content.civilizations.all.length - 1;
    $('ng-rivals').innerHTML = Array.from({ length: most }, (_, i) => i + 1)
      .map((n) => `<option value="${n}"${n === 3 ? ' selected' : ''}>${n} rival${n === 1 ? '' : 's'}</option>`)
      .join('');
    $<HTMLInputElement>('ng-seed').value = randomSeed();
    chosen = content.civilizations.all[0]!.key;
    built = true;
  }

  function renderCivs(): void {
    const content = options.getContent();
    $('ng-civs').innerHTML = content.civilizations.all
      .map((civ) => {
        const unique = describeUnique(content, civ);
        const traits = civ.traitIds
          .map((id) => {
            const trait = content.traits.get(id);
            return `<div class="ng-trait"><b>${escapeHtml(trait.name)}</b><ul>${describeTrait(content, trait)
              .map((l) => `<li>${escapeHtml(l)}</li>`)
              .join('')}</ul></div>`;
          })
          .join('');
        const selected = civ.key === chosen;
        return `<button class="ng-civ${selected ? ' selected' : ''}" data-civ="${civ.key}" role="radio" aria-checked="${selected}">
          <div class="ng-civ-head"><span class="ng-swatch" style="background:${civ.colour}"></span>
            <span><b>${escapeHtml(civ.name)}</b><span class="dim"> · ${escapeHtml(civ.leader)}</span></span></div>
          <div class="ng-traits">${traits}</div>
          ${unique ? `<div class="ng-unique"><b>${escapeHtml(unique.name)}</b> <span class="dim">replaces the ${escapeHtml(unique.replaces)}</span><div class="dim">${escapeHtml(unique.line)}</div></div>` : ''}
        </button>`;
      })
      .join('');
    for (const card of root.querySelectorAll<HTMLButtonElement>('button[data-civ]')) {
      card.addEventListener('click', () => {
        chosen = card.dataset['civ']!;
        renderCivs();
      });
      card.addEventListener('dblclick', () => {
        chosen = card.dataset['civ']!;
        start();
      });
    }
    const civ = content.civilizations.get(content.civilizations.id(chosen));
    $('ng-chosen').innerHTML = `Lead <b>${escapeHtml(civ.name)}</b> under ${escapeHtml(civ.leader)}.<div class="dim">${escapeHtml(civ.description)}</div>`;
  }

  function renderTypeNote(): void {
    const content = options.getContent();
    const type = content.mapgen.mapTypes.find((t) => t.key === $<HTMLSelectElement>('ng-type').value);
    $('ng-type-note').textContent = type?.summary ?? '';
  }

  function start(): void {
    const setup: GameSetup = {
      seed: $<HTMLInputElement>('ng-seed').value.trim() || randomSeed(),
      size: $<HTMLSelectElement>('ng-size').value,
      civs: Number($<HTMLSelectElement>('ng-rivals').value) + 1,
      civType: chosen,
      mapType: $<HTMLSelectElement>('ng-type').value,
    };
    close();
    options.start(setup);
  }

  function open(): void {
    if (!built) build();
    root.hidden = false;
    renderCivs();
    renderTypeNote();
    const button = $<HTMLButtonElement>('ng-continue');
    button.hidden = true;
    void options.latestAutosave().then((turn) => {
      if (turn === null) return;
      button.hidden = false;
      button.textContent = `Continue your last game (turn ${turn})`;
    });
    $('ng-start').focus();
  }
  function close(): void {
    root.hidden = true;
  }

  $('ng-close').addEventListener('click', close);
  $('ng-start').addEventListener('click', start);
  $('ng-random').addEventListener('click', () => {
    $<HTMLInputElement>('ng-seed').value = randomSeed();
  });
  $('ng-type').addEventListener('change', renderTypeNote);
  $('ng-continue').addEventListener('click', () => {
    close();
    options.continueLatest();
  });
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  return {
    open,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
  };
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
